import {
  type PostHogPersonProperties,
  type PostHogTenantGroupProperties,
} from "encamp-shared/src/instrumentation/events";
// Use the "no-external" build so posthog-js never lazy-loads its extensions
// (session replay, surveys, exception/dead-click autocapture, etc.) from
// `${api_host}/static/*.js` at runtime. Those URLs carry "posthog"/"recorder"
// in the path and are dropped by content/ad blockers even through the
// first-party reverse proxy (VITE_POSTHOG_HOST) — the blocker matches the URL in
// the browser before the request is sent, so no proxy can save them. Instead we
// bundle each extension below; they register on window.__PosthogExtensions__ at
// build time. Trade-off: the PostHog toolbar (dev-only visual editor) can't
// load, since it is runtime-only.
import posthog from "posthog-js/dist/module.no-external";
import "posthog-js/dist/posthog-recorder";
import "posthog-js/dist/surveys";
import "posthog-js/dist/exception-autocapture";
import "posthog-js/dist/dead-clicks-autocapture";
import "posthog-js/dist/tracing-headers";
import "posthog-js/dist/web-vitals";

/**
 * Client-side PostHog wrapper for Oak.
 *
 * Opt-in per environment: `init` no-ops unless `VITE_ENABLE_POSTHOG === "true"`
 * AND a `VITE_POSTHOG_KEY` is present, and every capture/identify/group call
 * short-circuits until then. `yarn encamp env` sets `VITE_ENABLE_POSTHOG` per
 * environment — "true" only for the release (prod) build, "false" everywhere
 * else — and local devs flip it to "true" in `apps/oak/.env.development.local`.
 *
 * The `environment` super-property is registered on init so every client event
 * carries the same discriminator server events use (filter dashboards to
 * `environment = release`). Server captures live in `encamp-posthog`
 * (posthog-node); this module owns browser-only signals: pageviews, UI clicks,
 * session length, and person/group identify.
 */

// Session/local storage keys owned by the analytics layer. Shared with
// AnalyticsIdentity (which writes them) so resetPosthog() can clear them on
// logout — otherwise the next user on a shared browser inherits the previous
// user's logged_in flag and last-session-length reading.
export const POSTHOG_LOGGED_IN_SESSION_KEY = "ph_logged_in";
export const POSTHOG_LAST_SESSION_MS_KEY = "ph_last_session_ms";

let initialized = false;

// Set from AnalyticsIdentity once the current user resolves, using the
// *original* user type — so a staff member emulating a customer (who otherwise
// looks like ordinary customer traffic on /o/:tenantId routes) is still treated
// as staff and dropped. Module-scoped rather than persisted (cf.
// opt_out_capturing, which writes localStorage and would bleed across users on
// a shared browser); it resets to false on reload until identify runs again.
let isStaffUser = false;

/**
 * Drop internal traffic client-side so it never reaches PostHog (saves
 * ingestion cost and keeps product analytics to real customers):
 *   - any event fired while on the staff admin surface (/staff/*)
 *   - any event from a staff user, everywhere (covers emulation on /o routes)
 * The only leak is the 1–2 anonymous pageviews before identify resolves
 * (login + maybe the first landing), which is acceptable.
 */
function isInternalTraffic(): boolean {
  if (isStaffUser) {
    return true;
  }
  const path = window.location.pathname;
  return path === "/staff" || path.startsWith("/staff/");
}

function isEnabled(): boolean {
  return initialized;
}

/** Flag the current session as staff so `before_send` drops its events. */
export function setPosthogStaffUser(value: boolean): void {
  isStaffUser = value;
}

export function initPosthog(): void {
  if (initialized) {
    return;
  }
  // Master on/off, set per environment by `yarn encamp env` (true only for the
  // release/prod build) and flippable locally in .env.development.local. Anything
  // other than the exact string "true" keeps analytics off.
  if (import.meta.env.VITE_ENABLE_POSTHOG !== "true") {
    return;
  }
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    return;
  }
  posthog.init(key, {
    // `||` (not `??`) so a blank VITE_POSTHOG_HOST also falls back — an empty
    // string would otherwise become api_host: "" and silently break sending.
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
    ui_host: "https://us.posthog.com",
    // SPA-aware pageviews: posthog-js hooks the History API (pushState/
    // replaceState/popstate) and fires $pageview on pathname change, so we don't
    // fire them by hand. Capture begins at init (main.tsx), which includes
    // pre-auth routes; those stay event-only via person_profiles below.
    capture_pageview: "history_change",
    // Pairs $pageleave with each $pageview so PostHog web analytics gets
    // scroll depth, time-on-page, and bounce.
    capture_pageleave: true,
    // Explicit events only — avoids high-volume DOM autocapture noise.
    autocapture: false,
    // Only create person profiles once we've identified a real user; anonymous
    // pre-auth traffic stays event-only.
    person_profiles: "identified_only",
    // Drop internal (staff / staff-app) traffic before it's sent. Returning
    // null discards the event; anything else passes it through unchanged.
    before_send: (event) => (event && isInternalTraffic() ? null : event),
  });
  posthog.register({ environment: import.meta.env.VITE_ENV ?? "unknown" });
  initialized = true;
}

export function capturePosthog(event: string, properties?: Record<string, unknown>): void {
  if (!isEnabled()) {
    return;
  }
  posthog.capture(event, properties);
}

export function identifyPosthog(distinctId: string, properties: PostHogPersonProperties): void {
  if (!isEnabled()) {
    return;
  }
  posthog.identify(distinctId, properties);
}

export function groupPosthog(tenantId: string, properties: PostHogTenantGroupProperties): void {
  if (!isEnabled()) {
    return;
  }
  posthog.group("tenant", tenantId, properties);
}

/**
 * Clear identity on logout so the next user on this browser isn't merged into
 * the previous user's PostHog person profile. Also clears our own session
 * bookkeeping keys (done unconditionally, so stale values never linger even if
 * PostHog was disabled when they were written).
 */
export function resetPosthog(): void {
  try {
    window.sessionStorage.removeItem(POSTHOG_LOGGED_IN_SESSION_KEY);
    window.localStorage.removeItem(POSTHOG_LAST_SESSION_MS_KEY);
  } catch {
    // Ignore storage failures (private mode, quota) — non-critical.
  }
  if (!isEnabled()) {
    return;
  }
  posthog.reset();
}
